import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchLeaderboard } from '@/lib/api';
import { getCoverageColor } from '@/lib/constants';
import CompanyCard from './CompanyCard';
import { ChevronLeft, ChevronRight, Globe2, ShieldCheck } from 'lucide-react';

const PAGE_SIZE = 12;

export default function ScorecardGrid({ selectedFilters, onCompanyClick, compareIds, onCompareToggle, onClearFilters, refreshTick }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [
    selectedFilters.country,
    selectedFilters.sector,
    selectedFilters.geography,
    selectedFilters.market_cap,
    selectedFilters.validation_status,
    selectedFilters.sort_by,
    selectedFilters.sort_order,
  ]);

  useEffect(() => {
    setLoading(true);
    const params = {
      page,
      page_size: PAGE_SIZE,
      sort_by: selectedFilters.sort_by || 'overall_score',
      sort_order: selectedFilters.sort_order || 'desc',
      completed_only: true,
    };

    ['country', 'sector', 'geography', 'market_cap', 'validation_status'].forEach((key) => {
      if (selectedFilters[key]) params[key] = selectedFilters[key];
    });

    fetchLeaderboard(params)
      .then(setPayload)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedFilters, page, refreshTick]);

  const coverageEntries = useMemo(
    () => Object.entries(payload?.country_context?.coverage_summary || {}),
    [payload]
  );

  if (loading) {
    return (
      <div className="space-y-5" data-testid="scorecard-loading-state">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} className="h-28 rounded-2xl bg-white/[0.06]" />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[...Array(8)].map((_, index) => (
            <Skeleton key={index} className="h-64 rounded-2xl bg-white/[0.06]" />
          ))}
        </div>
      </div>
    );
  }

  const companies = payload?.leaderboard || [];
  const countryContext = payload?.country_context;
  const total = payload?.total || 0;
  const totalPages = payload?.total_pages || 1;

  if (!companies.length) {
    return (
      <div className="rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.75)] px-6 py-14 text-center" data-testid="dashboard-empty-state">
        <p className="text-xl font-semibold text-white">No companies match the current analytics filters.</p>
        <p className="mt-2 text-sm text-white/45">Try broadening the country or validation filters to restore leaderboard coverage.</p>
        <Button type="button" onClick={onClearFilters} className="mt-5 rounded-xl bg-[#2A9D8F] text-white hover:bg-[#24897d]">
          Reset analytics filters
        </Button>
      </div>
    );
  }

  return (
    <section className="space-y-5" data-testid="scorecard-grid-section">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_1fr_1fr]">
        <div className="rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.88)] p-5 shadow-[0_10px_30px_rgba(0,0,0,0.32)]">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs uppercase tracking-[0.18em] text-white/35">Scorecard view</p>
            {countryContext ? (
              <Badge className="border border-white/10 bg-white/5 text-[#4ECDC4]" data-testid="scorecard-country-context">
                {countryContext.country}
              </Badge>
            ) : (
              <Badge className="border border-white/10 bg-white/5 text-[#3498DB]">Global benchmark</Badge>
            )}
          </div>
          <h2 className="mt-2 text-2xl font-bold text-white">
            {countryContext ? `${countryContext.country} ESG Leaderboard` : 'Cross-market Formula 1 ESG benchmark'}
          </h2>
          <p className="mt-2 max-w-3xl text-sm text-white/50">
            {countryContext?.coverage_note || 'Global benchmark deduplicates company entities across GP-country views so you can compare the strongest public ESG performers at a glance.'}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-white/55">
            <span className="font-mono text-white tabular-nums">{total}</span>
            <span>companies in view</span>
            {countryContext ? (
              <>
                <span className="text-white/25">&middot;</span>
                <span>{countryContext.region}</span>
              </>
            ) : null}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.88)] p-5 shadow-[0_10px_30px_rgba(0,0,0,0.32)]" data-testid="scorecard-coverage-panel">
          <div className="flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-[#3498DB]" />
            <p className="text-xs uppercase tracking-[0.18em] text-white/35">Coverage mix</p>
          </div>
          <div className="mt-4 space-y-3">
            {coverageEntries.length ? (
              coverageEntries.map(([level, count]) => (
                <div key={level} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                  <span className="text-sm text-white/60">{level}</span>
                  <Badge className="border-0 text-white" style={{ backgroundColor: getCoverageColor(level) }}>
                    {count}
                  </Badge>
                </div>
              ))
            ) : (
              <p className="text-sm text-white/45">Global benchmark aggregates the highest-scoring unique entities across all GP markets.</p>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.88)] p-5 shadow-[0_10px_30px_rgba(0,0,0,0.32)]" data-testid="scorecard-validation-panel">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#2A9D8F]" />
            <p className="text-xs uppercase tracking-[0.18em] text-white/35">Validation focus</p>
          </div>
          <p className="mt-4 font-mono text-3xl font-bold text-white tabular-nums">
            {countryContext ? countryContext.validated_count : companies.filter((company) => company.validation_status === 'validated').length}
          </p>
          <p className="mt-1 text-sm text-white/50">
            {countryContext ? 'validated records in this GP market view' : 'validated entities on the current global page'}
          </p>
          <p className="mt-4 text-xs text-white/35">All cards surface source type, verification state, country coverage, and full provenance in the company modal.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {companies.map((company, index) => (
          <motion.div
            key={company.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.02, duration: 0.28 }}
          >
            <CompanyCard
              company={company}
              rank={(page - 1) * PAGE_SIZE + index + 1}
              onClick={() => onCompanyClick(company)}
              isComparing={compareIds.includes(company.id)}
              onCompareToggle={() => onCompareToggle(company.id)}
            />
          </motion.div>
        ))}
      </div>

      <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.75)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid="scorecard-pagination">
        <div className="text-sm text-white/45">
          Showing <span className="font-mono text-white">{(page - 1) * PAGE_SIZE + 1}</span> to{' '}
          <span className="font-mono text-white">{Math.min(page * PAGE_SIZE, total)}</span> of{' '}
          <span className="font-mono text-white">{total}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page === 1}
            className="rounded-xl border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.08]"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Previous
          </Button>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white/65">
            Page <span className="font-mono text-white">{page}</span> / <span className="font-mono text-white">{totalPages}</span>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={page >= totalPages}
            className="rounded-xl border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.08]"
          >
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}
